<template>
    <span
        class="status-badge"
        data-table-inline-pill="true"
        :class="variantClass"
    >
        {{ displayLabel }}
    </span>
</template>

<script>
import { getAuditGapStatusVariant } from '@/utils/auditGapStatus';

const SUPPORTED_VARIANTS = [
    'neutral',
    'primary',
    'success',
    'warning',
    'danger',
    'dark',
];

/**
 * Read-only status pill. Uses `data-table-inline-pill` so TableList skips
 * multi-line clamp on `td span`.
 */
export default {
    name: 'StatusBadge',
    props: {
        label: {
            type: [String, Number],
            default: '',
        },
        variant: {
            type: String,
            default: '',
        },
        kind: {
            type: String,
            default: '',
        },
        status: {
            type: [String, Number],
            default: '',
        },
    },
    computed: {
        displayLabel() {
            const trimmed = String(this.label ?? '').trim();
            return trimmed || 'Unknown';
        },
        resolvedVariant() {
            const explicitVariant = String(this.variant ?? '').trim();
            if (explicitVariant) {
                return this.normalizeVariant(explicitVariant);
            }

            if (this.kind === 'audit-gap') {
                return this.normalizeVariant(
                    getAuditGapStatusVariant(this.status)
                );
            }

            return 'neutral';
        },
        variantClass() {
            return `status-badge--${this.resolvedVariant}`;
        },
    },
    methods: {
        normalizeVariant(variant) {
            const normalized = String(variant ?? '')
                .trim()
                .toLowerCase();
            return SUPPORTED_VARIANTS.includes(normalized)
                ? normalized
                : 'neutral';
        },
    },
};
</script>

<style scoped>
.status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-sizing: border-box;
    width: fit-content;
    max-width: 100%;
    justify-self: start;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
}

.status-badge--neutral {
    background: var(--neutral-500);
    color: var(--black);
}

.status-badge--primary {
    background: var(--primary-50);
    color: var(--primary-500);
}

.status-badge--success {
    background: var(--success);
    color: var(--white);
}

.status-badge--warning {
    background: var(--warning);
    color: var(--black);
}

.status-badge--danger {
    background: var(--danger);
    color: var(--white);
}

.status-badge--dark {
    background: var(--neutral-700);
    color: var(--white);
}
</style>
